import React, { useState, FC, useMemo, useEffect } from 'react';
import { TwAutocompleteOption, TwRenderOptionProps } from '../TwAutocomplete/TwAutocomplete';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import {
  ICreateLoadTag,
  ISearchTagsResponse,
  TAG_COLOR,
  TagsPalette
} from '../../redux/api/loadTags/loadTags.model';
import { useCreateTagMutation, useSearchTagsQuery } from '../../redux/api/loadTags/loadTagsApi';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useMediaQuery, Theme } from '@mui/material';
import {
  setLoadDetailReload,
  setTripDetailReload
} from '../../redux/features/navigation/navigationSlice';
import { useAddTripTagMutation } from '../../redux/api/trips/tripApi';
import { EntityType } from '../../redux/Global.model';
import useAuth from '../../providers/Auth/Auth.provider';

interface TwSearchCreateLoadTagProps {
  entity: EntityType;
  open: boolean;
  anchor: HTMLElement | null;
  changeOpen: (open: boolean) => void;
  loadId: string;
  tripId?: string;
  reloadData?: () => void;
}

const TwSearchCreateLoadTag: FC<TwSearchCreateLoadTagProps> = ({
  entity,
  open,
  anchor,
  changeOpen,
  loadId,
  tripId,
  reloadData
}): JSX.Element => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const dispatch = useAppDispatch();
  const tripsFilterParams = useAppSelector((state) => state.activeTrips.filters);
  const orderDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const tripDetailOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);
  const tagsAreSeparated = getTenantSetting('separate-tag-management-for-orders-and-trips');

  const params = { limit: 12, offset: 0, orderBy: 'created_at', sort: 'DESC' };
  const [textSearch, setTextSearch] = useState<string>('');
  const [tagsOptions, setTagsOptions] = useState<TwAutocompleteOption[]>([]);
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [addCreateTagOrder] = useCreateTagMutation();
  const [addCreateTagTrip] = useAddTripTagMutation();

  const { data: tagsResponse } = useSearchTagsQuery({
    params,
    filterParams: {
      search: textSearch
    }
  });
  const { addNotification } = useNotifications();

  useEffect(() => {
    if (tagsResponse) {
      // format response to match the autocomplete options
      const formattedTags = tagsResponse.content.map((tag) => {
        return {
          id: tag.id,
          label: tag.name,
          value: tag
        };
      });

      setTagsOptions(formattedTags);
    }
  }, [tagsResponse]);

  const createOrAddTag = (tag?: TwAutocompleteOption): void => {
    if (!loadId && entity === EntityType.ORDER) return;
    if (!tripId && entity === EntityType.TRIP) return;

    if (!tag && (textSearch.trim().length === 0 || textSearch.trim() === '')) {
      return addNotification({
        message: 'Tag name can not be empty',
        variant: 'error'
      });
    }

    if (textSearch.length > 12) {
      return addNotification({
        message: 'Tag name must be less than 12 characters',
        variant: 'error'
      });
    }

    const newTag = {} as ICreateLoadTag;
    if (entity === EntityType.TRIP) {
      newTag.tripId = tripId;
    } else {
      newTag.loadId = loadId;
    }

    // If textSearch is found in tagsResponse.content, then don't create a new tag, just add it to the load
    const foundTag = tagsOptions.find((tagOption) => tagOption.label === textSearch);

    if (foundTag) {
      newTag.tagId = foundTag.id;
    } else if (tag) {
      newTag.tagId = tag.id;
    } else {
      newTag.name = textSearch;
    }

    if (entity === EntityType.TRIP) {
      addCreateTagTrip({
        ...newTag,
        searchTripParams: {
          filters: {
            ...tripsFilterParams,
            includeArchived: matchesMobileOrTablet
          }
        }
      })
        .unwrap()
        .then((response) => {
          setTextSearch('');
          changeOpen(false);

          if (orderDetailOpen && !enableLtl && !tagsAreSeparated) {
            dispatch(setLoadDetailReload(true));
          }
          if (
            (orderDetailOpen && enableLtl) ||
            tripDetailOpen ||
            (orderDetailOpen && tagsAreSeparated)
          ) {
            dispatch(setTripDetailReload(true));
          }
        })
        .catch((error) => {
          addNotification({
            message: 'Error adding or creating tag to trip',
            variant: 'error'
          });
        });
    } else {
      addCreateTagOrder({
        ...newTag,
        searchTripParams: {
          filters: {
            ...tripsFilterParams,
            includeArchived: matchesMobileOrTablet
          }
        },
        tagsAreSeparated
      })
        .unwrap()
        .then((response) => {
          setTextSearch('');
          changeOpen(false);

          if (orderDetailOpen) {
            dispatch(setLoadDetailReload(true));
          } else {
            dispatch(setTripDetailReload(true));
          }
        })
        .catch((error) => {
          addNotification({
            message: 'Error adding or creating tag to order',
            variant: 'error'
          });
        });
    }
  };

  const nextColorToUse = useMemo(() => {
    const lastUsed = tagsOptions[tagsOptions.length - 1];

    const lastColorUsedObject = lastUsed ? (lastUsed.value as ISearchTagsResponse) : null;

    if (!lastColorUsedObject) {
      return TAG_COLOR.GREEN;
    }

    const lastColorIndex = Object.keys(TagsPalette).findIndex(
      (color) => color === lastColorUsedObject.color
    );

    const nextColor = Object.keys(TagsPalette)[lastColorIndex + 1];

    if (!nextColor) {
      return TAG_COLOR.GREEN;
    } else {
      return nextColor as keyof typeof TagsPalette;
    }
  }, [tagsOptions.length]);

  return (
    <ClickAwayListener onClickAway={(): void => changeOpen(false)}>
      <Popper
        disablePortal
        open={open}
        className="load-tags__search-popper"
        anchorEl={anchor}
        placement="bottom-start"
      >
        <Autocomplete
          options={tagsOptions}
          className="load-tags__autocomplete"
          fullWidth
          renderInput={(params): JSX.Element => {
            return (
              <TextField
                {...params}
                name={'search-create-load-tag'}
                placeholder={'Search or create new'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: params.InputProps.endAdornment
                }}
                sx={{
                  borderRadius: '4px'
                }}
                onKeyDown={(event): void => {
                  if (event.key === 'Enter') {
                    createOrAddTag();
                  }
                }}
              />
            );
          }}
          filterOptions={(x: any): any => x}
          renderOption={(props, option): React.ReactNode => {
            return <RenderTagOption key={option.id} option={option} props={props} />;
          }}
          onChange={(event, value): void => {
            if (value) {
              createOrAddTag(value);
              changeOpen(false);
            }
          }}
          disablePortal
          popupIcon={null}
          open={true}
          onInputChange={(_event, value): void => {
            setTextSearch(value);
          }}
          classes={{
            inputRoot: 'load-tags__input-default',
            listbox: 'load-tags__autocomplete__listbox',
            popper: 'load-tags__autocomplete__popper',
            noOptions: 'load-tags__autocomplete__noOptions'
          }}
          noOptionsText={
            <Box className="load-tags__autocomplete__noOptions-text">
              <Typography sx={{ marginRight: '3px' }}>
                Press <span>Enter</span> to create tag{' '}
              </Typography>
              <Chip
                sx={{
                  backgroundColor: TagsPalette[nextColorToUse].background,
                  color: TagsPalette[nextColorToUse].text,
                  borderColor: TagsPalette[nextColorToUse].border
                }}
                className="load-tags__option__tag"
                label={textSearch}
                variant="outlined"
              />
            </Box>
          }
        />
      </Popper>
    </ClickAwayListener>
  );
};

export default TwSearchCreateLoadTag;

const RenderTagOption: FC<TwRenderOptionProps> = ({ option, props }): JSX.Element => {
  const value: ISearchTagsResponse = option.value as ISearchTagsResponse;

  return (
    <MenuItem className="load-tags__option" {...props}>
      <Chip
        key={option.id}
        sx={{
          backgroundColor: TagsPalette[value.color].background,
          color: TagsPalette[value.color].text,
          borderColor: TagsPalette[value.color].border
        }}
        label={option.label}
        className="load-tags__option__tag"
        variant="outlined"
      />
    </MenuItem>
  );
};
